// Old path — a BODIED delegate. The menu now lives at
// `packages/agents/src/ui/projects/components/LinkContextButton.tsx`
// and is published through the single `.` door; what stays here is the supply of
// the five app-only inputs it takes as props, so no caller of this path changed.
//
// WHAT CROSSES AND WHY EACH ONE HAD TO:
//
//   the three analytics emits — they reach `trackWorkEvent`, which a rendered
//   surface in a package never does. The payloads (which token, which surface)
//   stay in the component, because those are what a suite pins and what a
//   mis-move would silently change.
//
//   the Sources panel — a render prop, bound beside this file in
//   `LinkContextButton.parts.tsx`, which is the path that already holds the Chat
//   coupling. See that file's header for why the binding sits there.
//
//   the picker rows — the DERIVED items rather than the queries behind them. The
//   derivation moved to `@heidi/agents/data`; the two source hooks did not, because
//   both are review-context branches over app atoms and RBAC. Reading them through
//   the old `useLinkableContextItems` path keeps the app to ONE reader of those
//   queries, which is also what keeps the consumer suites that stub the derivation
//   from booting the real workspace stack.
import {
  LINK_CONTEXT_PICKER_LIMIT,
  LinkContextButton as LinkContextButtonCore,
  useLinkableContextItems,
  type LinkContextButtonProps as LinkContextButtonCoreProps,
  type UseLinkableContextItemsArgs,
} from '@heidi/agents';

import {
  trackWorkHomeComposerCta,
  trackWorkHomeComposerRoutineActioned,
  trackWorkHomeComposerViewed,
} from '@/models/analytics/features/trackWorkHomeComposerAnalytics';
import { WorkSourcesPopover } from './LinkContextButton.parts';

export type { LinkedContextItem } from '@heidi/agents/contracts';

/**
 * The pre-move prop shape, kept under its old name: the five injected inputs are
 * this file's job, so no caller of this path passes anything new.
 */
export type LinkContextButtonProps = Omit<
  LinkContextButtonCoreProps,
  | 'onTrackCta'
  | 'onTrackRoutineActioned'
  | 'onTrackViewed'
  | 'pickerItems'
  | 'SourcesPopover'
> &
  Pick<UseLinkableContextItemsArgs, 'patientScope'>;

export const LinkContextButton = ({
  patientScope,
  ...props
}: LinkContextButtonProps): JSX.Element => {
  const pickerItems = useLinkableContextItems({
    limit: LINK_CONTEXT_PICKER_LIMIT,
    patientScope,
  });

  return (
    <LinkContextButtonCore
      {...props}
      pickerItems={pickerItems}
      onTrackCta={trackWorkHomeComposerCta}
      onTrackViewed={trackWorkHomeComposerViewed}
      onTrackRoutineActioned={trackWorkHomeComposerRoutineActioned}
      SourcesPopover={WorkSourcesPopover}
    />
  );
};
